import { Dispatch, MutableRefObject, SetStateAction } from "react";
import { FacilityShiftWithDocument } from "src/containers/facilityDashboard/ShiftCalendar/dayView/types";
import { WorkplaceUser } from "@src/appV2/InterviewSettings/api/useGetWorkplaceUsers";
import { WorkerTypeValues } from "@src/appV2/redesign/utils/WorkerTypes";
import { type UseNegotiationSpendLimitResult } from "@src/appV2/FacilityDashboard/VariableCharge/hooks/useNegotiationSpendLimit";

export enum NegotiationStatus {
  OPEN = "OPEN",
  EXPIRED = "EXPIRED",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  CLOSED = "CLOSED",
}

export enum NegotiationType {
  RATE = "RATE",
  /**
   * @deprecated Time negotiations have been disabled and deprecated. This value is kept for backward compatibility with existing data.
   */
  TIME = "TIME",
}

export enum NegotiationHistoryAction {
  STARTED = "STARTED",
  PROPOSED = "PROPOSED",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  BOOKED_BY_HCP = "BOOKED_BY_HCP",
  BOOKED_BY_ANOTHER_HCP = "BOOKED_BY_ANOTHER_HCP",
  BOOKED_CONFLICTING_SHIFT = "BOOKED_CONFLICTING_SHIFT",
  SHIFT_ASSIGNED_BY_ADMIN = "SHIFT_ASSIGNED_BY_ADMIN",
  SHIFT_LEAD_TIME_EXPIRED = "SHIFT_LEAD_TIME_EXPIRED",
  WORKER_SUSPENDED = "WORKER_SUSPENDED",
  WORKPLACE_SUSPENDED = "WORKPLACE_SUSPENDED",
  WORKER_DEACTIVATED = "WORKER_DEACTIVATED",
  WORKPLACE_DEACTIVATED = "WORKPLACE_DEACTIVATED",
  WORKER_BLOCKED = "WORKER_BLOCKED",
  WORKPLACE_BLOCKED = "WORKPLACE_BLOCKED",
}

export enum NegotiationRateRole {
  WORKER = "WORKER",
  WORKPLACE = "WORKPLACE",
  ADMIN = "ADMIN",
}

export interface NegotiationHistoryItem {
  _id: string;
  action: NegotiationHistoryAction;
  createdByUserId?: string;
  createdByUserRole?: NegotiationRateRole;
  payRate?: number;
  start?: string;
  end?: string;
  createdAt: string;
}

interface NegotiationMetadata {
  workerName?: string;
  workerPhoto?: string;
  workerRating?: number;
  lastOffer?: NegotiationHistoryItem;
  workerQualificationForShift?: string;
}

export interface Negotiation {
  _id: string;
  status: NegotiationStatus;
  type: NegotiationType;
  shiftId: string;
  workerId: string;
  workplaceId?: string;
  multiplier?: number;
  history: NegotiationHistoryItem[];
  initialChargeRate: number;
  initialPayRate: number;
  initialStart?: string;
  initialEnd?: string;
  createdAt?: string;
  updatedAt?: string;
  metadata?: NegotiationMetadata;
}

export interface NegotiationWithShiftDetails {
  _id: string;
  status: NegotiationStatus;
  shiftId: string;
  workerId: string;
  workplaceId: string;
  shift: {
    _id: string;
    start: string;
    name: string;
    end: string;
    agentReq: WorkerTypeValues;
  };
}

export interface INegotiationHistoryProps {
  negotiation: Negotiation;
  parentRef?: MutableRefObject<HTMLDivElement | null>;
  admin: boolean;
  facilityTimezone: string;
  workplaceUsersMap?: Map<string, WorkplaceUser>;
}

export interface INegotiationsListProps {
  admin: boolean;
  negotiations: Negotiation[];
  selectedNegotiation: Negotiation;
  setSelectedNegotiationId: Dispatch<SetStateAction<string>>;
  showWorkerRatings?: boolean;
}

export enum ListNegotiationsCustomField {
  LAST_OFFER = "LAST_OFFER",
  WORKER_DETAILS = "WORKER_DETAILS",
  SHIFT_DETAILS = "SHIFT_DETAILS",
}

export interface IProposalComponentProps {
  negotiation: Negotiation;
  updateNegotiationInState: (updatedNegotiationData: Negotiation, setSelected?: boolean) => void;
  shift: FacilityShiftWithDocument;
  maxCharge: number;
  negotiations: Negotiation[];
  spendLimit: UseNegotiationSpendLimitResult;
}

export interface INegotiationActionsProps {
  negotiation: Negotiation;
  otherNegotiations: Negotiation[];
  updateNegotiationInState: (updatedNegotiationData: Negotiation, setSelected?: boolean) => void;
  handleRefetchFacilityShifts?: () => void;
  chargeBreakdown?: IChargeBreakdown;
  shift: FacilityShiftWithDocument;
  spendLimit: UseNegotiationSpendLimitResult;
  /**
   * Whether to render the spend-limit card here. The proposal component owns the (interactive) card
   * when it's shown, so the accept surface suppresses its duplicate to avoid a stacked second card.
   */
  showSpendLimitCard: boolean;
}

export interface IRateSummaryProps {
  admin: boolean;
  chargeBreakdown?: IChargeBreakdown;
  stateCode?: string;
}

export interface IRateNegotiationModalProps {
  admin?: boolean;
  isOpen: boolean;
  shift: FacilityShiftWithDocument;
  workerTypeLabel: string;
  date: string;
  onClose: () => void;
  negotiations: Negotiation[];
  setNegotiations: Dispatch<SetStateAction<Negotiation[]>>;
  handleRefetchFacilityShifts?: (lastRefreshedDateCellAt?: boolean) => void;
  selectedShiftNegotiationId?: string;
}

export interface IChargeBreakdown {
  payRate: string;
  deltaPayRate: string;
  initialChargeRate: string;
  deltaChargeRate: string;
  totalCharge: string;
  maxCharge: string;
}

export interface INegotiationAcceptedEvent {
  negotiation: Negotiation;
  otherNegotiations: Negotiation[];
}

export interface INegotiationEndedEvent {
  negotiation: Negotiation;
  otherNegotiations: Negotiation[];
}

export interface INegotiationProposedEvent {
  negotiation: Negotiation;
  rate: number;
  negotiations: Negotiation[];
}

export enum RateNegotiationConfigEnum {
  ON = "ON",
  OFF = "OFF",
}
